import React, { useState, useRef, useEffect, memo } from 'react';

const Image = memo(function Image({
  src,
  alt = 'Image',
  className = '',
  style = {},
  placeholderColor = '#f1f5f9',
  fallbackSrc,
  priority = false,
  ...props
}) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [activeSrc, setActiveSrc] = useState(src);
  const imgRef = useRef(null);

  useEffect(() => {
    setActiveSrc(src);
    setHasError(false);
    setIsLoaded(false);
  }, [src]);

  useEffect(() => {
    if (!imgRef.current) return;

    const rect = imgRef.current.getBoundingClientRect();
    if (priority || (rect.width > 0 && rect.top < window.innerHeight + 200 && rect.bottom > -200)) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px', threshold: 0.01 },
    );

    observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, [priority, activeSrc]);

  const handleLoad = () => setIsLoaded(true);

  const handleError = () => {
    if (fallbackSrc && activeSrc !== fallbackSrc) {
      setActiveSrc(fallbackSrc);
      setIsLoaded(false);
      return;
    }
    setHasError(true);
    setIsLoaded(true);
  };

  if (!isInView || hasError || !activeSrc) {
    return (
      <div
        ref={isInView ? undefined : imgRef}
        className={`${className} flex items-center justify-center`}
        style={{ ...style, backgroundColor: placeholderColor }}
        aria-label={alt}
      >
        <svg
          xmlns="https://www.w3.org/2000/svg"
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#cbd5e1"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21 15 16 10 5 21" />
        </svg>
      </div>
    );
  }

  return (
    <img
      ref={imgRef}
      src={activeSrc}
      alt={alt}
      className={`${className} transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
      style={{ ...style, backgroundColor: !isLoaded ? placeholderColor : 'transparent' }}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      fetchPriority={priority ? 'high' : 'auto'}
      onLoad={handleLoad}
      onError={handleError}
      {...props}
    />
  );
});

export default Image;
